// THE APP STORE LINK CARRIES THE VISIT'S SOURCE (2026-09-29).
//
// PostHog follows a visit as far as `store_badge_click` and no further: the
// install happens inside the App Store, where the site cannot see. Apple can.
// App Store Connect → App Analytics → Acquisition → Campaigns counts product
// page views and first-time downloads per campaign token (`ct`) for every
// listing link that also carries the team's provider token (`pt`). Without a
// `ct`, every download the site sends is one line — "Web Referrer:
// capnolog.com" — and Reddit, Instagram and TikTok are merged inside it.
//
// So a store link is tagged at the moment it is pressed with `ct=web-<source>`:
// the visit's `utm_source` when the link that brought them carried one, else
// the social site's name when the referrer was a social host, else the
// six-way referrer class. docs/ops/campaign-links.md lists the tagged links
// each channel posts, and it is the naming contract the tokens read back as.
//
// PURE and import-free, like referrer-class.ts, so
// `__tests__/store-campaign.test.mjs` imports it straight from TypeScript
// through Node's type stripping. The caller (hooks/useStoreCampaignLinks.ts)
// supplies the acquisition properties, the listing URL and the provider token.

/** The three acquisition super properties usePageView registers, as read back. */
export interface CampaignSource {
  readonly utm_source: string;
  readonly referrer_host: string;
  readonly referrer_class: string;
}

/** Apple's limit on a campaign token. */
export const CT_MAX_LENGTH = 40;

/** Every token the site writes starts here, so a hand-built link never collides. */
export const WEB_CT_PREFIX = "web-";

/**
 * Social hosts whose second-level label is not the name anyone reads in a
 * report: `t.co` is X, `youtu.be` is YouTube, `lnkd.in` is LinkedIn.
 */
const SOCIAL_ALIASES: Readonly<Record<string, string>> = {
  t: "x",
  twitter: "x",
  youtu: "youtube",
  lnkd: "linkedin",
  ycombinator: "hackernews",
  studentdoctor: "sdn",
};

/** Lowercase letters, digits and single dashes — the shape `slugify()` produces. */
function slug(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * The site's name from its host: `old.reddit.com` → reddit, `l.instagram.com`
 * → instagram, `vm.tiktok.com` → tiktok, `t.co` → x. The second-level label,
 * through the alias table. Empty when the host has no second-level label.
 */
export function socialName(host: string): string {
  const labels = String(host ?? "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "")
    .split(".")
    .filter(Boolean);
  if (labels.length < 2) return "";
  const name = labels[labels.length - 2];
  return SOCIAL_ALIASES[name] ?? name;
}

/**
 * The campaign token for this visit. Order is load-bearing:
 *
 *  1. `utm_source`, because a tagged link is the one answer the owner chose;
 *  2. the social site's name, so an untagged share still says which site;
 *  3. the referrer class (search, direct, ai-assistant, other, internal).
 *
 * Always `web-` plus a slug, never longer than Apple's 40 characters.
 */
export function storeCampaignToken(source: CampaignSource): string {
  const utm = slug(source.utm_source);
  let name: string;
  if (utm && utm !== "none") {
    name = utm;
  } else if (source.referrer_class === "social") {
    name = slug(socialName(source.referrer_host)) || "social";
  } else {
    name = slug(source.referrer_class) || "other";
  }
  return (WEB_CT_PREFIX + name).slice(0, CT_MAX_LENGTH).replace(/-+$/, "");
}

/** Origin plus path, trailing slash dropped, so `/app/id1` and `/app/id1/` agree. */
function listingKey(url: URL): string {
  return `${url.origin}${url.pathname.replace(/\/+$/, "")}`;
}

/**
 * The tagged listing URL, or null when this link must be left alone:
 *
 *  - no provider token, or one that is not all digits — Apple drops a `ct` it
 *    cannot pair with a `pt`, and a placeholder must never ship in a link;
 *  - the href is not the listing (the redeem sheet, any other Apple page);
 *  - the href already names a campaign — a hand-built campaign link wins.
 *
 * `mt=8` is the media type Apple's own campaign-link generator writes.
 */
export function withStoreCampaign(
  href: string,
  listingUrl: string,
  providerToken: string | null,
  campaign: string,
): string | null {
  if (!providerToken || !/^\d+$/.test(providerToken)) return null;
  if (!campaign) return null;
  let link: URL;
  let listing: URL;
  try {
    link = new URL(href);
    listing = new URL(listingUrl);
  } catch {
    return null;
  }
  if (listingKey(link) !== listingKey(listing)) return null;
  if (link.searchParams.has("ct")) return null;
  link.searchParams.set("pt", providerToken);
  link.searchParams.set("ct", campaign.slice(0, CT_MAX_LENGTH));
  link.searchParams.set("mt", "8");
  return link.toString();
}

/** An App Store Connect custom product page id: a UUID. */
const PRODUCT_PAGE_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * THE AUDIENCE'S OWN PRODUCT PAGE (D-319, 2026-10-16). The listing URL with
 * `?ppid=<id>` opens one custom product page, with its own screenshots and
 * promotional text, in place of the default page. Apple shows the default
 * page for an id that is not approved and visible, so a stale id costs the
 * tailored screenshots and nothing else.
 *
 * The listing unchanged for a missing or malformed id; null for no listing.
 * `withStoreCampaign` matches on origin and path, so a press still adds
 * pt, ct and mt beside the ppid.
 */
export function withProductPage(
  listingUrl: string | null,
  productPageId: string | null | undefined,
): string | null {
  if (!listingUrl) return null;
  if (!productPageId || !PRODUCT_PAGE_ID.test(productPageId)) return listingUrl;
  let link: URL;
  try {
    link = new URL(listingUrl);
  } catch {
    return listingUrl;
  }
  link.searchParams.set("ppid", productPageId.toLowerCase());
  return link.toString();
}
